/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import * as S from "./styles";
import { CostumePageService } from "../../services/costumePageService";
import {
  ITicketOrder,
  ITicketOrderLine,
  ITicketIssued,
} from "../../pages/Costume/components/SelectTicketsComp/types";
import { formatEventDateTime } from "../../helpers/eventDate.helper";

export interface IEventTicketsSectionProps {
  boxId?: string;
  ticketOrders?: ITicketOrder[];
  title?: string;
  className?: string;
  // Staff view (#740): loads the Box's orders with staff permission and offers
  // Mas Camp cash for a HELD order.
  staff?: boolean;
  // Masquerader view (#736): for an unpaid hold, say it awaits payment and until when.
  showHoldInfo?: boolean;
}

export const EventTicketsSection: React.FC<IEventTicketsSectionProps> = ({
  boxId,
  ticketOrders: propOrders,
  title = "Attached Event Tickets",
  className,
  staff = false,
  showHoldInfo = false,
}) => {
  const [orders, setOrders] = useState<ITicketOrder[]>(propOrders || []);
  const [loading, setLoading] = useState<boolean>(!propOrders && !!boxId);
  const [error, setError] = useState<string | null>(null);
  const [payingOrderId, setPayingOrderId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadOrders = useCallback(
    (id: string) =>
      staff
        ? CostumePageService.getInstance().getBoxTicketOrders(id)
        : CostumePageService.getInstance().getMyTicketOrders(id),
    [staff]
  );

  const takeCash = async (order: ITicketOrder) => {
    if (!boxId || payingOrderId) return;
    const amount = `${order.currency || "TTD"} ${Number(order.total).toFixed(2)}`;
    if (!window.confirm(`Record ${amount} cash for these tickets?`)) return;
    setPayingOrderId(order.ticketOrderId);
    try {
      const res = await CostumePageService.getInstance().payTicketsAtMasCamp({
        boxId,
        ticketOrderId: order.ticketOrderId,
        allocations: [],
      });
      toast.success(res?.message || "Payment recorded");
    } catch (err: any) {
      const msg: string = err?.message || "";
      if (msg.includes("HOLD_EXPIRED")) {
        toast.error("This ticket hold has expired. Ask the masquerader to reserve again.");
      } else if (msg.includes("INVALID_STATE") || msg.includes("LOCKED_BY_OTHER_TX")) {
        toast.error("These tickets are already paid or a payment is in progress.");
      } else {
        toast.error(msg || "Could not record the payment");
      }
    } finally {
      setPayingOrderId(null);
      setReloadKey((k) => k + 1);
    }
  };

  useEffect(() => {
    if (propOrders) {
      setOrders(propOrders);
      setLoading(false);
      return;
    }

    if (!boxId) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    loadOrders(boxId)
      .then((data) => {
        if (isMounted) {
          setOrders(data || []);
          setError(null);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch ticket orders for box:", boxId, err);
        if (isMounted) {
          setError(err?.message || "Failed to load ticket orders");
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [boxId, propOrders, loadOrders, reloadKey]);

  // If no ticket orders exist or still loading in background, render nothing
  // to ensure costume-only views are completely untouched
  if (!orders || orders.length === 0) {
    return null;
  }

  const formatCurrency = (amount: number = 0, currency: string = "TTD") => {
    return `${currency} ${Number(amount).toFixed(2)}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return formatEventDateTime(dateString);
  };

  const getStatusDisplay = (status: string) => {
    switch (status?.toUpperCase()) {
      case "PAID":
      case "CONFIRMED":
      case "SUCCESS":
        return { label: "CONFIRMED", status: "CONFIRMED" };
      case "HELD":
      case "PAYING":
      case "PAID_SYNC_PENDING":
        return { label: "RESERVED", status: "HELD" };
      case "RELEASED":
      case "EXPIRED":
        return { label: "EXPIRED", status: "EXPIRED" };
      default:
        return { label: status || "UNKNOWN", status };
    }
  };

  return (
    <S.TicketsContainer
      id="event-tickets-section"
      className={className}
      data-testid="event-tickets-section"
    >
      <S.SectionHeader>
        <S.SectionTitle>
          <span>🎟️</span>
          {title}
        </S.SectionTitle>
      </S.SectionHeader>

      {orders.map((order: ITicketOrder, idx: number) => {
        const statusInfo = getStatusDisplay(order.status);
        const orderCurrency = order.currency || "TTD";

        return (
          <S.TicketOrderCard
            key={order.ticketOrderId || `order-${idx}`}
            data-testid="event-ticket-order"
          >
            <S.OrderHeader>
              <S.EventInfo>
                <S.FeteName>
                  {order.feteName || "Carnival Event"}
                </S.FeteName>
                <S.EventMeta>
                  {order.feteStartsAt && (
                    <S.MetaItem>
                      📅 {formatDate(order.feteStartsAt)}
                    </S.MetaItem>
                  )}
                  {(order as any).venue && (
                    <S.MetaItem>
                      📍 {(order as any).venue}
                    </S.MetaItem>
                  )}
                  {order.ticketOrderId && (
                    <S.MetaItem>
                      Ref: #{order.ticketOrderId.slice(-6).toUpperCase()}
                    </S.MetaItem>
                  )}
                </S.EventMeta>
              </S.EventInfo>

              <S.StatusBadge
                $status={statusInfo.status}
                data-testid="ticket-status-badge"
              >
                {statusInfo.label}
              </S.StatusBadge>
            </S.OrderHeader>

            {showHoldInfo && order.status === "HELD" && (
              <div
                style={{ fontSize: "13px", color: "#b45309", margin: "6px 0" }}
                data-testid="ticket-hold-info"
              >
                Awaiting payment
                {order.holdExpiresAt
                  ? ` – reserved until ${formatDate(order.holdExpiresAt)}. Pay before then or the tickets are released.`
                  : "."}
              </div>
            )}

            {order.lines && order.lines.length > 0 && (
              <S.LinesTable>
                {order.lines.map((line: ITicketOrderLine, lineIdx: number) => (
                  <S.LineRow key={line.tierId || `line-${lineIdx}`}>
                    <div>
                      <S.LineTier>{line.tierName || "General Tier"}</S.LineTier>
                      <S.LineDetail>
                        Qty: {line.quantity} × {formatCurrency(line.unitPrice, orderCurrency)}
                      </S.LineDetail>
                    </div>
                    <S.LinePrice>
                      {formatCurrency(line.lineTotal, orderCurrency)}
                    </S.LinePrice>
                  </S.LineRow>
                ))}

                {Number(order.convenienceFee) > 0 && (
                  <S.LineRow>
                    <div>
                      <S.LineTier>Convenience Fee</S.LineTier>
                    </div>
                    <S.LinePrice>
                      {formatCurrency(Number(order.convenienceFee), orderCurrency)}
                    </S.LinePrice>
                  </S.LineRow>
                )}
                <S.TotalRow>
                  <S.TotalLabel>Ticket Total</S.TotalLabel>
                  <S.TotalAmount>
                    {formatCurrency(order.total, orderCurrency)}
                  </S.TotalAmount>
                </S.TotalRow>

                {staff && order.status === "HELD" && (
                  <button
                    type="button"
                    className="btn btn-warning btn-sm mt-2"
                    disabled={!!payingOrderId}
                    onClick={() => takeCash(order)}
                    data-testid="take-ticket-cash"
                  >
                    {payingOrderId === order.ticketOrderId
                      ? "Recording..."
                      : `Take cash for tickets (${formatCurrency(order.total, orderCurrency)})`}
                  </button>
                )}
              </S.LinesTable>
            )}

            {order.tickets && order.tickets.length > 0 && (
              <S.TicketTokensList>
                {order.tickets.map((tck: ITicketIssued, tckIdx: number) => (
                  <S.TicketToken key={tck.ticketId || `tck-${tckIdx}`}>
                    #{tck.ticketId} ({tck.tierName})
                  </S.TicketToken>
                ))}
              </S.TicketTokensList>
            )}

            {order.viewTicketsUrl && (
              <S.OrderActions>
                <S.ViewTicketsBtn
                  onClick={() => {
                    if (typeof window !== "undefined" && order.viewTicketsUrl) {
                      window.open(order.viewTicketsUrl, "_blank", "noopener,noreferrer");
                    }
                  }}
                  data-testid="view-tickets-btn"
                >
                  View / Download Tickets ↗
                </S.ViewTicketsBtn>
              </S.OrderActions>
            )}
          </S.TicketOrderCard>
        );
      })}
    </S.TicketsContainer>
  );
};

export default EventTicketsSection;
